import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, fmtDate } from "@/lib/api";
import { PageTitle, Skeleton } from "@/components/portal/ui";

function monthLabel(m) {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

export default function MinistrySchedule() {
  const [params] = useSearchParams();
  const ministryId = params.get("id");
  const ministryName = params.get("name") || "Pelayanan";
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/portal/ministry/calendar", { params: { ministry_id: ministryId, month } })
      .then((r) => setData(r.data)).catch(() => setData(false));
  }, [ministryId, month]);

  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div data-testid="portal-ministry-schedule">
      <div className="px-5 pt-10 flex items-center gap-3">
        <Link to="/portal/ministry" data-testid="ministry-schedule-back" className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ArrowLeft className="w-5 h-5" /></Link>
        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">{ministryName}</p>
      </div>
      <PageTitle eyebrow="Jadwal Bulanan" title={ministryName} />

      <div className="px-5 mt-2 flex items-center justify-between">
        <button onClick={() => shift(-1)} data-testid="ministry-schedule-prev" className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ChevronLeft className="w-4 h-4" /></button>
        <p className="font-extrabold text-slate-900 capitalize" data-testid="ministry-schedule-month">{monthLabel(month)}</p>
        <button onClick={() => shift(1)} data-testid="ministry-schedule-next" className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="px-5 mt-4 pb-10">
        {data === null ? <Skeleton className="h-64" /> : data === false ? (
          <div className="bg-white rounded-3xl p-6 border border-slate-100 text-sm text-slate-500" data-testid="ministry-schedule-error">Anda bukan anggota divisi ini.</div>
        ) : data.mode === "image" ? (
          <div className="bg-white rounded-3xl overflow-hidden border border-slate-100" data-testid="ministry-schedule-image">
            <img src={fileUrl(data.image_url)} alt="Jadwal bulanan" className="w-full" />
          </div>
        ) : data.days.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-slate-100" data-testid="ministry-schedule-empty">
            <ImageIcon className="w-8 h-8 mx-auto text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Belum ada jadwal untuk bulan ini.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ministry-schedule-days">
            {data.days.map((d, i) => (
              <div key={i} className="bg-white rounded-3xl p-4 border border-slate-100" data-testid={`ministry-schedule-day-${i}`}>
                <p className="text-xs font-bold text-blue-600">{fmtDate(d.date, { weekday: "long", day: "numeric", month: "long" })}</p>
                <p className="mt-1 font-extrabold text-slate-900">{d.service}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {d.team.map((t, j) => (
                    <span key={j} className="text-[11px] font-bold bg-slate-100 text-slate-600 rounded-full px-2.5 py-1" data-testid={`ministry-schedule-member-${i}-${j}`}>{t.name} · {t.role}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
